import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import './TopNav.css'

function TopNav({ onMenuClick, onCollapseClick, collapsed }) {
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const searchRef = useRef(null)
  const notificationsRef = useRef(null)
  const userMenuRef = useRef(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
        setTimeout(() => searchRef.current?.focus(), 100)
      }
      if (e.key === 'Escape') {
        setSearchOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const notifications = [
    {
      id: 1,
      type: 'payment',
      title: 'Payment received',
      message: '$450.00 from Widget Co',
      time: '10 min ago',
      unread: true
    },
    {
      id: 2,
      type: 'team',
      title: 'Team member joined',
      message: 'Alex Rivera accepted your invitation',
      time: '1 hour ago',
      unread: true
    },
    {
      id: 3,
      type: 'billing',
      title: 'Invoice ready',
      message: 'Your January invoice is available',
      time: '2 days ago',
      unread: false
    }
  ]

  const unreadCount = notifications.filter(n => n.unread).length

  return (
    <header className="top-nav">
      <div className="top-nav-left">
        {/* Mobile menu button */}
        <button className="nav-button mobile-menu" onClick={onMenuClick} aria-label="Open menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>

        {/* Collapse button (desktop) */}
        <button className="nav-button collapse-toggle" onClick={onCollapseClick} aria-label="Toggle sidebar">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {collapsed ? (
              <>
                <polyline points="13 17 18 12 13 7"/>
                <polyline points="6 17 11 12 6 7"/>
              </>
            ) : (
              <>
                <polyline points="11 17 6 12 11 7"/>
                <polyline points="18 17 13 12 18 7"/>
              </>
            )}
          </svg>
        </button>

        {/* Business Switcher */}
        <div className="business-switcher">
          <button className="business-button">
            <div className="business-icon">A</div>
            <span className="business-name">Acme Corp</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="top-nav-center">
        {/* Search */}
        <div className={`search-container ${searchOpen ? 'open' : ''}`}>
          <button 
            className="search-trigger" 
            onClick={() => {
              setSearchOpen(true)
              setTimeout(() => searchRef.current?.focus(), 100)
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <span className="search-placeholder">Search...</span>
            <span className="search-shortcut">⌘K</span>
          </button>
          
          {searchOpen && (
            <div className="search-modal">
              <div className="search-modal-backdrop" onClick={() => setSearchOpen(false)} />
              <div className="search-modal-content">
                <div className="search-input-wrapper">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    ref={searchRef}
                    type="text"
                    className="search-input"
                    placeholder="Search transactions, customers, invoices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button className="search-close" onClick={() => setSearchOpen(false)}>
                    <kbd>Esc</kbd>
                  </button>
                </div>
                <div className="search-results">
                  <div className="search-section">
                    <span className="search-section-title">Quick Actions</span>
                    <div className="search-items">
                      <button className="search-item">
                        <span className="search-item-icon">+</span>
                        Create new checkout
                      </button>
                      <button className="search-item">
                        <span className="search-item-icon">+</span>
                        Send invoice
                      </button>
                      <button className="search-item">
                        <span className="search-item-icon">+</span>
                        Record expense
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="top-nav-right">
        {/* Help */}
        <button className="nav-button" aria-label="Help">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </button>

        {/* Notifications */}
        <div className="notifications-dropdown" ref={notificationsRef}>
          <button 
            className={`nav-button ${unreadCount > 0 ? 'has-unread' : ''}`}
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            aria-label="Notifications"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
          </button>

          {notificationsOpen && (
            <div className="dropdown-menu notifications-menu">
              <div className="dropdown-header">
                <span className="dropdown-title">Notifications</span>
                <button className="mark-read">Mark all read</button>
              </div>
              <div className="notifications-list">
                {notifications.map(notification => (
                  <div key={notification.id} className={`notification-item ${notification.unread ? 'unread' : ''}`}>
                    <div className={`notification-icon ${notification.type}`}>
                      {notification.type === 'payment' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="1" x2="12" y2="23"/>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                        </svg>
                      )}
                      {notification.type === 'team' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                      )}
                      {notification.type === 'billing' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="1" y="4" width="22" height="16" rx="2"/>
                          <line x1="1" y1="10" x2="23" y2="10"/>
                        </svg>
                      )}
                    </div>
                    <div className="notification-content">
                      <span className="notification-title">{notification.title}</span>
                      <span className="notification-message">{notification.message}</span>
                      <span className="notification-time">{notification.time}</span>
                    </div>
                    {notification.unread && <span className="unread-dot" />}
                  </div>
                ))}
              </div>
              <Link to="/dashboard/notifications" className="dropdown-footer">
                View all notifications
              </Link>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="user-dropdown" ref={userMenuRef}>
          <button 
            className="user-button"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            aria-label="User menu"
          >
            <div className="user-avatar">SC</div>
          </button>

          {userMenuOpen && (
            <div className="dropdown-menu user-menu">
              <div className="user-menu-header">
                <div className="user-avatar-lg">SC</div>
                <div className="user-info">
                  <span className="user-name">Sarah Chen</span>
                  <span className="user-email">sarah@acmecorp.com</span>
                </div>
              </div>
              <div className="dropdown-divider" />
              <div className="dropdown-section">
                <Link to="/dashboard/settings" className="dropdown-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Profile
                </Link>
                <Link to="/dashboard/settings" className="dropdown-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4"/>
                  </svg>
                  Settings
                </Link>
                <Link to="/dashboard/billing" className="dropdown-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                  Billing
                </Link>
              </div>
              <div className="dropdown-divider" />
              <div className="dropdown-section">
                <button className="dropdown-item logout">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default TopNav
